import axios from 'axios';

import { errorMessages } from '~/constants/errorMessages.ts';
import type { TIITSign } from '~/store/iit/types.ts';
import {
  GuaranteesApplicationStatus,
  guaranteesApplicationStatuses,
  type TGuaranteesPageStore,
  type TGuaranteesApplication,
  type TGuaranteesApplicationsResponse,
  type TGuaranteesBeneficiary,
  type TGuaranteesBeneficiaryResponse,
  type TGuaranteesTimelineEvent,
  type TGuaranteesManagerTasksResponse,
  type TGuaranteesAdministrationTasksResponse,
  type TGuaranteesApplicationTenderLotsPayload,
  type TGuaranteesSpringPageResponse,
  type TGuaranteesSegmentSupervisor,
  type TGuaranteesSupervisorGroupKey,
  type TGuaranteesSupervisorSegment,
  type TFetchGuaranteesAdministrationTasksRequest,
  type TFetchGuaranteesManagerTasksRequest,
  type TFetchGuaranteesMainDealsRequest,
  TInternalApprovalStatusField,
  type TGuaranteesApplicationSelectionPatchResult,
} from './types.ts';
import { initialState } from '~/store/guarantees/initialState.ts';

const getRecordValue = (value: unknown, key: string): unknown =>
  value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined;

const getStringValue = (value: unknown): string => String(value ?? '').trim();
const GUARANTEES_EMPTY_VALUE = '-';

export const buildGuaranteesMainDealsRequestKey = ({
  contragentId,
  page = 0,
  size = 20,
  sort,
}: TFetchGuaranteesMainDealsRequest): string =>
  JSON.stringify({
    contragentId,
    page,
    size,
    sort: sort ?? [],
  });

export const buildGuaranteesApplicationTenderLotsPayload = (
  selectedLotIds: string[]
): TGuaranteesApplicationTenderLotsPayload => ({
  tender: {
    lots: selectedLotIds
      .map((lotId) => lotId.trim())
      .filter(Boolean)
      .map((id) => ({ id })),
  },
});

export const getGuaranteesApplicationDisplayNumber = (
  application?: Pick<TGuaranteesApplication, 'applicationNumber' | 'localId' | 'root'> | null
): { applicationNumber: string; isTemporaryApplicationNumber: boolean } => {
  const applicationNumber = getStringValue(application?.applicationNumber);
  const rootApplicationNumber = getStringValue(application?.root?.applicationNumber);
  const localId = getStringValue(application?.localId);

  if (applicationNumber) {
    return {
      applicationNumber,
      isTemporaryApplicationNumber: false,
    };
  }

  if (rootApplicationNumber) {
    return {
      applicationNumber: rootApplicationNumber,
      isTemporaryApplicationNumber: false,
    };
  }

  if (localId) {
    return {
      applicationNumber: localId,
      isTemporaryApplicationNumber: true,
    };
  }

  return {
    applicationNumber: GUARANTEES_EMPTY_VALUE,
    isTemporaryApplicationNumber: false,
  };
};

export const buildFilesSigningParams = ({
  fileIds,
  applicationId,
}: {
  fileIds?: string[];
  applicationId?: string;
}) => {
  const params = new URLSearchParams();

  fileIds?.forEach((fileId) => {
    params.append('fileIds', fileId);
  });

  if (applicationId) {
    params.append('applicationId', applicationId);
  }

  return params;
};

export const getApplicationFileParams = (
  fileId?: string
): Record<string, string> | undefined => {
  const normalizedFileId = fileId?.trim();

  return normalizedFileId ? { fileId: normalizedFileId } : undefined;
};

export const getDownloadedApplicationFileName = (
  contentDisposition: string | undefined,
  fallbackFileId: string
): string => {
  const filenameMatch = contentDisposition?.match(/filename="(.+)"/);

  return filenameMatch?.[1] ?? `document_${fallbackFileId}`;
};

const createSignatureFile = (fileObj: TIITSign): File => {
  const mimeType = 'application/pkcs7-signature';
  const byteString = atob(fileObj.base64File);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const bytes = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i);
  }

  return new File([arrayBuffer], fileObj.name, { type: mimeType });
};

export const getGuaranteesSignedDocumentsFormData = (files: TIITSign[]): FormData => {
  const formData = new FormData();

  files.forEach((fileObj) => {
    const documentId = getStringValue(fileObj.documentId);

    if (!documentId) {
      throw new Error(errorMessages.DOCUMENT_SIGNING_FAILED);
    }

    formData.append(documentId, createSignatureFile(fileObj));
  });

  return formData;
};

export const GUARANTEES_SUPERVISOR_SEGMENT_API_GROUPS: Record<
  TGuaranteesSupervisorGroupKey,
  string
> = {
  usapok: 'usapok',
  uo: 'uo',
  umvn: 'umvn',
  udbtf: 'udbtf',
  uakdo: 'uakdo',
};

export const getGuaranteesSupervisorSegmentsApiGroup = (
  groupKey: TGuaranteesSupervisorGroupKey
) => GUARANTEES_SUPERVISOR_SEGMENT_API_GROUPS[groupKey];

export const normalizeGuaranteesSupervisorSegments = (
  data: unknown
): TGuaranteesSupervisorSegment[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item) => ({
    segment: {
      id: String(item?.segment?.id ?? ''),
      displayName: String(item?.segment?.displayName ?? ''),
    },
    supervisors: Array.isArray(item?.supervisors)
      ? item.supervisors.map(
          (supervisor: TGuaranteesSegmentSupervisor & { supervisorEmail?: string }) => ({
            id: supervisor.id,
            email: supervisor.email ?? supervisor.supervisorEmail ?? '',
            supervisorEmail: supervisor.supervisorEmail,
            createdAt: supervisor.createdAt,
            updatedAt: supervisor.updatedAt,
          })
        )
      : [],
  }));
};

export const normalizeGuaranteesApplicationStatus = (
  status?: GuaranteesApplicationStatus | string | null
): GuaranteesApplicationStatus =>
  guaranteesApplicationStatuses.includes(status as GuaranteesApplicationStatus)
    ? (status as GuaranteesApplicationStatus)
    : GuaranteesApplicationStatus.DRAFT;

export const getGuaranteesApplicationIdentity = (
  application?: Pick<TGuaranteesApplication, 'applicationId' | 'id'> | null
): string => getStringValue(application?.applicationId || application?.id);

export const mapGuaranteesApplicationToRow = (
  application: TGuaranteesApplication
): TGuaranteesApplicationsResponse['applications'][number] => {
  const root = application.root;
  const guaranteeTerms = application.guaranteeTerms;
  const guaranteeAmount = Number(getRecordValue(guaranteeTerms, 'guaranteeAmount'));
  const id = getGuaranteesApplicationIdentity(application);
  const {
    applicationNumber,
    isTemporaryApplicationNumber,
  } = getGuaranteesApplicationDisplayNumber(application);

  return {
    id,
    applicationNumber,
    isTemporaryApplicationNumber,
    tenderId:
      getStringValue(getRecordValue(root, 'tenderPublicId')) ||
      getStringValue(getRecordValue(root, 'prozorroTenderId')),
    createdAt: getStringValue(application.createdAt),
    guaranteeAmount: Number.isFinite(guaranteeAmount) ? guaranteeAmount : 0,
    status: normalizeGuaranteesApplicationStatus(application.applicationStatus),
  };
};

export const normalizeGuaranteesApplicationsResponse = (
  response: TGuaranteesSpringPageResponse<TGuaranteesApplication>,
  page: number,
  size: number
): TGuaranteesApplicationsResponse => {
  const applications = (response.content ?? []).map(mapGuaranteesApplicationToRow);
  const totalElements = response.totalElements ?? applications.length;

  return {
    applications,
    count: totalElements,
    pageConfiguration: {
      page: response.number ?? page,
      size: response.size ?? size,
      totalElements,
    },
  };
};

export const normalizeGuaranteesManagerTasksResponse = (
  response: TGuaranteesSpringPageResponse<TGuaranteesApplication>,
  page: number,
  size: number
): TGuaranteesManagerTasksResponse => {
  const tasks = response.content ?? [];
  const totalElements = response.totalElements ?? tasks.length;

  return {
    tasks,
    count: totalElements,
    pageConfiguration: {
      page: response.number ?? page,
      size: response.size ?? size,
      totalElements,
    },
  };
};

export const normalizeGuaranteesAdministrationTasksResponse = (
  response: TGuaranteesSpringPageResponse<TGuaranteesApplication>,
  page: number,
  size: number
): TGuaranteesAdministrationTasksResponse => {
  const tasks = response.content ?? [];
  const totalElements = response.totalElements ?? tasks.length;

  return {
    tasks,
    count: totalElements,
    pageConfiguration: {
      page: response.number ?? page,
      size: response.size ?? size,
      totalElements,
    },
  };
};

export const buildGuaranteesAdministrationTasksParams = ({
  adminRoles,
  ...request
}: TFetchGuaranteesAdministrationTasksRequest): URLSearchParams => {
  const params = buildGuaranteesManagerTasksParams(request);

  adminRoles.forEach((role) => params.append('adminRoles', role));

  return params;
};

export const buildGuaranteesManagerTasksParams = ({
  ids,
  page,
  search,
  size,
  sort,
  status,
}: TFetchGuaranteesManagerTasksRequest): URLSearchParams => {
  const params = new URLSearchParams();

  ids?.forEach((id) => {
    const normalizedId = id.trim();

    if (normalizedId) {
      params.append('ids', normalizedId);
    }
  });
  params.append('page', String(page));
  params.append('size', String(size));
  sort?.forEach((sortValue) => params.append('sort', sortValue));

  if (search?.trim()) {
    params.append('search', search.trim());
  }

  if (status) {
    params.append('status', status);
  }

  return params;
};

const getNumberValue = (value: unknown): number | undefined => {
  const normalizedValue = Number(value);

  return Number.isFinite(normalizedValue) ? normalizedValue : undefined;
};

const normalizeGuaranteesTimelineEvent = (event: unknown): TGuaranteesTimelineEvent | null => {
  if (!event || typeof event !== 'object') {
    return null;
  }

  const record = event as Record<string, unknown>;

  return {
    changedAt: getStringValue(record.changedAt),
    applicationId: getStringValue(record.applicationId),
    description: getStringValue(record.description),
    changedByName: getStringValue(record.changedByName),
    changedByEmail: getStringValue(record.changedByEmail),
    sequenceNumber: getNumberValue(record.sequenceNumber),
    statusFrom: getStringValue(record.statusFrom),
    statusTo: getStringValue(record.statusTo),
  };
};

export const normalizeGuaranteesApplicationTimelineResponse = (
  response: unknown
): TGuaranteesTimelineEvent[] => {
  const events = (Array.isArray(response) ? response : response ? [response] : [])
    .map(normalizeGuaranteesTimelineEvent)
    .filter((event): event is TGuaranteesTimelineEvent => !!event);

  return events.sort((left, right) => {
    const leftSequence = getNumberValue(left.sequenceNumber);
    const rightSequence = getNumberValue(right.sequenceNumber);

    if (leftSequence !== undefined || rightSequence !== undefined) {
      return (leftSequence ?? Number.MAX_SAFE_INTEGER) - (rightSequence ?? Number.MAX_SAFE_INTEGER);
    }

    const leftChangedAt = new Date(left.changedAt ?? '').getTime();
    const rightChangedAt = new Date(right.changedAt ?? '').getTime();

    return (
      (Number.isFinite(leftChangedAt) ? leftChangedAt : Number.MAX_SAFE_INTEGER) -
      (Number.isFinite(rightChangedAt) ? rightChangedAt : Number.MAX_SAFE_INTEGER)
    );
  });
};

export const getGuaranteesPayloadVersion = (payload: unknown): number | undefined => {
  const version = (payload as { version?: unknown } | undefined)?.version;

  return typeof version === 'number' && Number.isFinite(version) ? version : undefined;
};

export const isGuaranteesApplicationPayload = (
  payload: unknown
): payload is TGuaranteesApplication => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const record = payload as Record<string, unknown>;

  return typeof record.id === 'string' && typeof record.version === 'number';
};

export const normalizeGuaranteesBeneficiaryResponse = (
  beneficiary?: TGuaranteesBeneficiary | TGuaranteesBeneficiaryResponse | null
): TGuaranteesBeneficiaryResponse => {
  if (!beneficiary) {
    return [];
  }

  return Array.isArray(beneficiary) ? beneficiary : [beneficiary];
};

type TGuaranteesBeneficiaryCountryNameSource = Pick<TGuaranteesBeneficiary, 'countryName'>;

export const preserveGuaranteesBeneficiaryCountryNames = (
  beneficiaries: TGuaranteesBeneficiaryResponse,
  fallback?:
    | TGuaranteesBeneficiaryCountryNameSource
    | readonly TGuaranteesBeneficiaryCountryNameSource[]
    | null
): TGuaranteesBeneficiaryResponse => {
  const fallbackBeneficiaries = Array.isArray(fallback) ? fallback : fallback ? [fallback] : [];

  return beneficiaries.map((beneficiary, index) => {
    if (getStringValue(beneficiary.countryName)) {
      return beneficiary;
    }

    const countryName = getStringValue(fallbackBeneficiaries[index]?.countryName);

    return countryName ? { ...beneficiary, countryName } : beneficiary;
  });
};

export const isMissingGuaranteesBaseRelationsError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const responseData = error.response?.data as
    | {
        code?: unknown;
        message?: unknown;
      }
    | undefined;
  const message = typeof responseData?.message === 'string' ? responseData.message : '';

  const isBaseRelationsNotFoundMessage =
    message.includes('BaseRelationModel') && message.includes('not found');

  return (
    isBaseRelationsNotFoundMessage &&
    ((error.response?.status === 408 && responseData?.code === 'REQUEST_TIMEOUT') ||
      (error.response?.status === 400 && responseData?.code === 'BAD_REQUEST'))
  );
};

export const getGuaranteesUdbtfTemplateDocumentFileName = (
  contentDisposition: string | undefined,
  applicationId: string
): string => {
  const filenameMatch = contentDisposition?.match(/filename="(.+)"/);

  return filenameMatch?.[1] ?? `udbtf_template_${applicationId}.pdf`;
};

export const getGuaranteesAmTemplateDocumentFileName = (
  contentDisposition: string | undefined,
  applicationId: string
): string => {
  const filenameMatch = contentDisposition?.match(/filename="(.+)"/);

  return filenameMatch?.[1] ?? `am_approval_letter_${applicationId}.pdf`;
};

export const getGuaranteesConclusionTemplateDocumentFileName = (
  contentDisposition: string | undefined,
  applicationId: string,
  approvalType: string
): string => {
  const filenameMatch = contentDisposition?.match(/filename="(.+)"/);

  return filenameMatch?.[1] ?? `${approvalType}_conclusion_template_${applicationId}.pdf`;
};

export const updateCurrentGuaranteesApplicationVersion = (
  state: TGuaranteesPageStore,
  payload: unknown
) => {
  const version = getGuaranteesPayloadVersion(payload);

  if (version !== undefined && state.currentApplication.data) {
    state.currentApplication.data.version = version;
  }
};

export const updateGuaranteesApplicationInTasks = (
  tasks: typeof initialState.administrationTasks.data.tasks,
  application: typeof initialState.currentApplication.data
) => {
  if (!application?.id) {
    return;
  }

  const task = tasks.find(
    (currentTask) =>
      currentTask.id === application.id || currentTask.applicationId === application.id
  );

  if (task) {
    Object.assign(task, application);
  }
};

export const syncCurrentApplicationInternalApprovalStatus = (
  state: typeof initialState,
  payload: unknown,
  applicationId: string,
  statusField: TInternalApprovalStatusField,
  applicationApprovalStatus: string
) => {
  const currentApplication = state.currentApplication.data;
  const isCurrentApplication =
    currentApplication &&
    (currentApplication.id === applicationId || currentApplication.applicationId === applicationId);

  if (!isCurrentApplication) {
    return;
  }

  if (
    isGuaranteesApplicationPayload(payload) &&
    (payload.id === applicationId || payload.applicationId === applicationId)
  ) {
    Object.assign(currentApplication, payload);
  } else {
    updateCurrentGuaranteesApplicationVersion(state, payload);
  }

  currentApplication[statusField] = applicationApprovalStatus;
};

export const getCurrentApplicationAfterSelectionPatch = (
  currentApplication: TGuaranteesApplication | null,
  patchResult: TGuaranteesApplicationSelectionPatchResult
): TGuaranteesApplication | null => patchResult.application ?? currentApplication;
